// order-domain: ownership=kkren copyPolicy=copy-and-prune-mgb pruneAfter=brand-retirement
// 巧巧郎混合下单拆单：同一次勾选里同时有家具与非家具包裹时，家具走海運（本单），
// 其余例外件由客人决定「另建一張海快訂單」或「先留在倉庫」。规则见 docs/ai/order-mixed-split.md。
// 跨页文案与纯格式化工具放在 order-shared/splitOrderCopy.js（复制根外组件也要用），这里只做转出口。
import { formatParcelNames, formatTwd, getParcelDisplayName, joinParcelNames } from '@g/order-shared/splitOrderCopy'

export {
  getParcelDisplayName,
  formatTwd,
  formatParcelNames,
  buildSplitSecondBannerText,
  buildSplitContinueText,
  buildSplitPendingText,
} from '@g/order-shared/splitOrderCopy'
export const SPLIT_MODES = Object.freeze({
  SEA: 'sea',
  SE: 'se',
})

export const SPLIT_DECISIONS = Object.freeze({
  SECOND: 'second',
  KEEP: 'keep',
})

// 第二单默认海快线路要排除的资费标签，与 shippingRates 过滤规则保持一致。
const SECOND_ORDER_EXCLUDED_LABELS = new Set([
  'HZKZQ',
  'HZKHD',
  'MB-ECONOMY',
  'ECONOMY-ALL',
  'SHOPEE26',
  'ENT28',
  'ENT1688',
  'AUTOSHIP',
  'CB1688',
])

export function isFurnitureParcel (parcel) {
  return Boolean(parcel?.isFurniture)
}

export function canParcelUseSe (parcel) {
  return Number(parcel?.extraInfo?.canTransportSe) === 1
}

// 只有海運倉且同时存在家具与非家具包裹才算混合；例外件里连海快也不能走的只能留倉。
export function classifyMixedSplit ({ parcels = [], jyAddrid, seaAddrids = [] } = {}) {
  const list = Array.isArray(parcels) ? parcels : []
  const furniture = list.filter(isFurnitureParcel)
  const others = list.filter(parcel => !isFurnitureParcel(parcel))
  const secondable = others.filter(canParcelUseSe)
  const keepOnly = others.filter(parcel => !canParcelUseSe(parcel))
  const isSeaAddr = (seaAddrids || []).map(String).includes(String(jyAddrid))
  return {
    mixed: isSeaAddr && furniture.length > 0 && others.length > 0,
    isSeaAddr,
    furniture,
    others,
    secondable,
    keepOnly,
  }
}

export function pickSecondOrderSeRate (shippingRates = []) {
  const list = Array.isArray(shippingRates) ? shippingRates : []
  const preferred = list.find(rate => {
    const label = String(rate?.srLabel || '')
    return rate?.transport === 'se' &&
      String(rate.srTwAddrid ?? '0') === '0' &&
      !SECOND_ORDER_EXCLUDED_LABELS.has(label) &&
      !label.startsWith('BIZECO') &&
      !label.startsWith('TBPDD')
  })
  return preferred || list.find(rate => rate?.transport === 'se') || null
}

export function buildSplitCopy ({
  others = [],
  secondable = [],
  keepOnly = [],
  seaCount = 0,
  seaFee = 0,
  estimateTotal = null,
  estimateRowsText = '',
  decision = '',
} = {}) {
  const otherLabel = formatParcelNames(others)
  const secondLabel = formatParcelNames(secondable)
  const single = others.length === 1
  const singleName = single ? getParcelDisplayName(others[0]) : ''
  const estimateText = estimateTotal == null ? '' : `${formatTwd(estimateTotal)} 元`
  const estimateNote = estimateRowsText
    ? `預估含${estimateRowsText}，優惠券與加購以第二單頁面為準。`
    : '實際金額以第二單頁面為準。'
  const seaFeeText = `${formatTwd(seaFee)} 元`
  const isSecond = decision === SPLIT_DECISIONS.SECOND
  const countOrLabel = others.length >= 3 ? `${others.length} 件` : otherLabel

  return {
    statusPending: `${others.length} 件包裹無法走海運，選海運時須另外安排。`,
    otherCardTitle: `不可走海運 ${others.length} 件`,
    otherCardNote: '選海運時須另行處理',
    bottomSelectLock: '請先選擇運輸方式',
    statusLine: single
      ? `1 件包裹無法走海運：${singleName}`
      : `${others.length} 件包裹無法走海運`,
    statusTag: decision ? '已處理' : '尚未處理',
    seaCountNote: decision ? '' : `（不含 ${others.length} 件尚未處理）`,
    rowPending: `${countOrLabel}尚未處理`,
    rowPendingAction: '選擇處理方式',
    rowDecided: isSecond
      ? `${secondLabel}：另建一張海快訂單${estimateText ? `，${estimateText}` : ''}`
      : `${otherLabel}：先留在倉庫，之後再寄`,
    rowDecidedSub: isSecond && estimateText
      ? `另有海快訂單 ${estimateText}，於本單付款後建立`
      : '',
    bottomLock: `${countOrLabel}尚未處理，處理後才能下一步`,
    dialogTitle: single ? `${singleName}怎麼處理？` : `這 ${others.length} 件怎麼處理？`,
    dialogBody: `${single ? singleName : `這 ${others.length} 件`}不是家具類商品，海運只收家具，需改走海快。其餘 ${seaCount} 件照常走海運，本單 ${seaFeeText}不變。`,
    optSecond: secondable.length > 1
      ? `另建一張海快訂單寄這 ${secondable.length} 件${estimateText ? ` ${estimateText}` : ''}`
      : `另建一張海快訂單${estimateText ? `，${estimateText}` : ''}，需另外付款`,
    optSecondSub: `海快宅配到府，本單付款後接著建立；兩單分開寄出、到貨時間不同。${estimateNote}`,
    optKeep: '先留在倉庫，之後再寄',
    optKeepSub: `本單只寄 ${seaCount} 件；${otherLabel}留在包裹列表，之後可再下單。`,
    keepOnlyTag: '僅能留倉',
    keepOnlyNote: keepOnly.length
      ? `${joinParcelNames(keepOnly)}連海快也無法承運，只能先留在倉庫。`
      : '',
    bannerSecond: `本單：海運 ${seaCount} 件，${seaFeeText}。另有一張海快訂單（${secondLabel}）${estimateText ? `${estimateText}，` : ''}本單付款後建立。`,
    bannerKeep: `倉庫還有 ${others.length} 件未安排運送：${joinParcelNames(others)}。`,
    step2Line: `本單付款後會接著建立第二單：海快 ${secondable.length} 件（${joinParcelNames(secondable)}）${estimateText ? `${estimateText}，` : ''}需另外付款。`,
  }
}

// 例外件卡片里每件包裹的标注：只在处理后显示结果（另建海快訂單／留倉），未处理时不加标注。
export function buildSplitParcelNote ({ parcel, decision = '', keepOnlyPclids = [] } = {}) {
  const keepOnly = keepOnlyPclids.map(String).includes(String(parcel?.pclid))
  if (decision) {
    const second = decision === SPLIT_DECISIONS.SECOND && !keepOnly
    return { text: second ? '另建海快訂單' : '留倉', resolved: true }
  }
  return { text: '', resolved: false }
}
